import type { PackingSlipItem } from './types';
import { RETURN_CENTER_ADDRESS } from './constants';
import { HD_LOGO } from './styles';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Escape special HTML characters to prevent markup injection. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ---------------------------------------------------------------------------
// HTML fragment builders
// ---------------------------------------------------------------------------

export function renderHeader(): string {
  return `
    <div class="header">
      <span class="header-title">Military Exchange APO/FPO/DPO Return Packing Slip</span>
      ${HD_LOGO}
    </div>
  `;
}

export function renderOrderInfo(orderId: string, orderBarcode: string): string {
  const safeOrderId = escapeHtml(orderId);
  return `
    <div class="order-info">
      <div class="order-number"><span class="order-label">Order</span><span class="order-id">#${safeOrderId}</span></div>
      <img class="barcode-img" src="data:image/png;base64,${orderBarcode}" alt="Order barcode" />
    </div>
  `;
}

export function renderAssociateNotice(): string {
  return `
    <div class="associate-notice">
      <strong>Military Exchange APO/FPO/DPO Order</strong>
      Associates must transfer this item to the Service Desk and process the Return/RTV in OrderUp.
    </div>
  `;
}

export function renderTableRows(items: PackingSlipItem[]): string {
  return items
    .map(
      (item) => `
    <tr>
      <td class="col-product">${escapeHtml(item.name)} ${escapeHtml(
        item.description,
      )}</td>
      <td class="col-qty">${item.quantity}</td>
      <td class="col-weight">${item.weight}</td>
      <td class="col-value">$${item.value.toFixed(2)}</td>
    </tr>`,
    )
    .join('');
}

export function renderTable(items: PackingSlipItem[]): string {
  return `
      <table class="items-table">
        <thead>
          <tr>
            <th class="col-product">Product</th>
            <th class="col-qty">Quantity</th>
            <th class="col-weight">Weight (lbs)</th>
            <th class="col-value">Value (USD)</th>
          </tr>
        </thead>
        <tbody>${renderTableRows(items)}</tbody>
      </table>
  `;
}

export function renderItemsTable(
  items: PackingSlipItem[],
  continued: boolean,
): string {
  const title = continued ? 'Return Items (Continued)' : 'Return Items';

  return `
    <div class="page-section-title section-title">${title}</div>
    <div class="page-items-table">
      ${renderTable(items)}
    </div>
  `;
}

export function renderScreenTable(items: PackingSlipItem[]): string {
  return `
    <div class="screen-table">
      <div class="section-title">Return Items</div>
      ${renderTable(items)}
    </div>
  `;
}

export function renderViewMore(): string {
  return '<div class="view-more">View more items next page</div>';
}

export function renderReturnInstructions(orderId: string): string {
  const safeOrderId = escapeHtml(orderId);
  return `
    <div class="return-instructions">
      <div class="cut-line">
        <div class="cut-line-label">Place a copy of this packing slip inside each return box!</div>
      </div>
      <div class="instructions-title">Return Instructions</div>
      <ol class="instructions-list">
        <li><strong>Pack:</strong> Place this packing slip inside the box with your item(s).</li>
        <li><strong>Address:</strong> Ship the package to the Return Center address listed below.</li>
        <li><strong>Ship:</strong> Take the package to an APO/FPO/DPO. You are responsible for shipping costs.</li>
        <li><strong>Customs Form:</strong> All APO/FPO/DPO shipments require a U.S. Customs Declaration Form.
          Use the product details on this packing slip when completing the form at the post office or on <u>USPS.com</u>.
          <ol type="a">
            <li>Include Order Number #${safeOrderId} in the customs form reference field.</li>
          </ol>
        </li>
      </ol>
      <div class="address-block">
        <div class="address-label">ADDRESS TO:</div>
        <div>First name: <strong>APO/FPO Return</strong></div>
        <div>Last name: <strong>${safeOrderId}</strong></div>
        <div>Company: <strong>${RETURN_CENTER_ADDRESS.company}</strong></div>
        <div>Address Line 1: <strong>${RETURN_CENTER_ADDRESS.addressLine1}</strong></div>
        <div>City: <strong>${RETURN_CENTER_ADDRESS.city}</strong></div>
        <div>State: <strong>${RETURN_CENTER_ADDRESS.state}</strong></div>
        <div>ZIP: <strong>${RETURN_CENTER_ADDRESS.zip}</strong></div>
      </div>
    </div>
  `;
}
